import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, Clock, Sparkles, ShoppingBag, Palette } from 'lucide-react';

const PersonalStylingPage = () => {
  const services = [
    {
      name: 'Closet Cleanse',
      price: '$600',
      duration: '4 hours',
      additionalRate: '+$75/additional hour',
      description: 'A comprehensive in-home coaching session where we walk through your entire wardrobe.',
      features: [
        'Complete wardrobe audit',
        'Learn what works for your body & style',
        'Closet organization & arrangement',
        'Donation & resell handling included',
        'Style goals assessment'
      ],
      highlight: 'Most Popular',
      icon: <Sparkles className="h-6 w-6" />
    },
    {
      name: 'Mini Shopping Spree',
      price: '$300',
      duration: '2 hours',
      additionalRate: 'or 20-25 virtual items',
      description: 'Perfect for a quick wardrobe refresh or filling in specific gaps.',
      features: [
        '2 hours of personal shopping',
        'In-store or virtual shopping list',
        'Outfit coordination guidance',
        'Budget-conscious selections'
      ],
      highlight: null,
      icon: <ShoppingBag className="h-6 w-6" />
    },
    {
      name: 'Full Shopping Spree',
      price: '$600',
      duration: '4 hours',
      additionalRate: 'or 40-45 virtual items',
      description: 'Complete wardrobe building for those ready for a significant style transformation.',
      features: [
        '4 hours of personal shopping',
        'In-store or virtual shopping list',
        'Complete outfit combinations',
        'Seasonal wardrobe planning',
        '+$150 for pre-pulled looks'
      ],
      highlight: null,
      icon: <ShoppingBag className="h-6 w-6" />
    }
  ];

  return (
    <div data-testid="personal-styling-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-6">
                Personal Styling
              </p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#2C3E50] mb-8 leading-tight">
                Transform Your Wardrobe
              </h1>
              <p className="font-sans text-[#57534E] text-lg leading-relaxed mb-8">
                Imagine walking into your closet every morning and loving everything you see. 
                No more "I have nothing to wear" moments. Just confidence, clarity, and clothes 
                that make you feel like your best self.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="styling-cta-btn"
                  className="rounded-full bg-[#2C3E50] hover:bg-[#1a252f] text-white px-8 py-6 text-base transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  Book a Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1618236444721-4a8dba415c15?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Luxury walk-in closet with built-in shelves"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section data-testid="pricing-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              Services & Pricing
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50]">
              Styling Packages
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-package-${index}`}
                className={`relative bg-[#F9F7F2] rounded-2xl p-8 md:p-10 ${
                  service.highlight === 'Most Popular' ? 'ring-2 ring-[#D4A373]' : ''
                }`}
              >
                {service.highlight && (
                  <span className="absolute -top-3 left-8 bg-[#D4A373] text-white text-xs font-sans uppercase tracking-wider px-4 py-1 rounded-full">
                    {service.highlight}
                  </span>
                )}
                
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-[#2C3E50] mb-2">
                      {service.name}
                    </h3>
                    <div className="flex items-center gap-2 text-[#57534E] text-sm">
                      <Clock className="h-4 w-4" />
                      <span>{service.duration}</span>
                      <span className="text-[#D4A373]">({service.additionalRate})</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-serif text-4xl text-[#2C3E50]">{service.price}</span>
                  </div>
                </div>

                <p className="font-sans text-[#57534E] mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 font-sans text-[#57534E]">
                      <Check className="h-5 w-5 text-[#84A98C] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact">
                  <Button
                    data-testid={`book-${service.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                    className="w-full rounded-full bg-[#2C3E50] hover:bg-[#1a252f] text-white py-6 transition-all duration-300"
                  >
                    Book This Service
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              The Process
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50]">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We discuss your style goals, lifestyle, and what\'s not working in your current wardrobe.' },
              { step: '02', title: 'Assessment', desc: 'I evaluate your existing pieces and identify what to keep, donate, and what gaps to fill.' },
              { step: '03', title: 'Transformation', desc: 'Together, we organize your closet and create outfit combinations you\'ll love.' },
              { step: '04', title: 'Confidence', desc: 'Walk away with a wardrobe that makes getting dressed a joy, not a chore.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <span className="font-serif text-5xl text-[#D4A373]/30">{item.step}</span>
                <h3 className="font-serif text-xl text-[#2C3E50] mt-4 mb-3">{item.title}</h3>
                <p className="font-sans text-[#57534E] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#2C3E50] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Ready for Your Wardrobe Transformation?
          </h2>
          <p className="font-sans text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Let's create a closet you'll love. Book your consultation today 
            and take the first step toward effortless style.
          </p>
          <Link to="/contact">
            <Button
              data-testid="styling-final-cta-btn"
              className="rounded-full bg-[#D4A373] hover:bg-[#c29260] text-white px-10 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              Book Your Session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PersonalStylingPage;
